/* eslint-disable */
/* eslint-disable eslint-comments/no-unused-disable */
/* 
* The file is automatically generated by Serenity UI.
* 
* ⚠️ WARNING: Any manual changes may be overwritten during the next upgrade
* 
* Found an issue? Submit a task to the ClickUp board:
* https://app.clickup.com/1255586/v/l/6-901510012553-1?pr=90154030902
*/
import * as stylex from '@stylexjs/stylex';
import { type ForwardedRef, createContext, forwardRef, useCallback, useEffect, useRef, useState } from 'react';
import { useFocusVisible, useFocusWithin, useHover } from 'react-aria';
import { type ContextValue, DEFAULT_SLOT, Provider, Select as SelectCore, type SelectProps as SelectCoreProps, TooltipTriggerStateContext, useContextProps } from 'react-aria-components';
import { CheckmarkStrokeIcon } from '../../icons/checkmark-stroke/checkmark-stroke.icon';
import { CloseStrokeIcon } from '../../icons/close-stroke/close-stroke.icon';
import { EditStrokeIcon } from '../../icons/edit-stroke/edit-stroke.icon';
import { MoreVerticalStrokeIcon } from '../../icons/more-vertical-stroke/more-vertical-stroke.icon';
import { $ } from '../../tokens.stylex';
import { HintContext } from '../hint/hint.component';
import { IconButtonContext } from '../icon-button/icon-button.component';
import { IconContext } from '../icon/icon.component';
import { LabelContext } from '../label/label.component';
import { ListBoxContext } from '../list-box/list-box.component';
import { PopoverContext } from '../popover/popover.component';
import { SelectInputStyleContext } from '../select-input/select-input.component';
import { SkeletonContext } from '../skeleton/skeleton.component';
import { TextContext } from '../text/text.component';
import { TooltipContext } from '../tooltip/tooltip.component';
import { View, ViewContext } from '../view/view.component';

interface InlineTableSelectProps extends Omit<SelectCoreProps<object>, 'style' | 'className'> {
  style?: stylex.StyleXStyles;
  variant?: 'primary' | 'warning' | 'success' | 'error';
}

const InlineTableSelectContext = createContext<ContextValue<InlineTableSelectProps, HTMLDivElement>>({});

const InlineTableSelect = forwardRef((props: InlineTableSelectProps, ref: ForwardedRef<HTMLDivElement>) => {
  [props, ref] = useContextProps(props, ref, InlineTableSelectContext);

  const { style, children, variant, onPointerEnter, onPointerLeave, onOpenChange, ...coreProps } = props;

  const [isEditing, setIsEditing] = useState(false);
  const [isFocusWithin, setFocusWithin] = useState(false);
  const [isEditable, setIsEditable] = useState(false);
  const [isSelectOpen, setIsSelectOpen] = useState(false);
  const exitHandledRef = useRef(false);
  const isSelectOpenRef = useRef(false);
  const escapeCloseRef = useRef(false);

  const actionButtonRef = useCallback((node: HTMLButtonElement | null) => setIsEditable(!!node), []);
  const [hasPreview, setHasPreview] = useState(false);
  const previewRef = useCallback((node: HTMLDivElement | null) => setHasPreview(!!node), []);

  const selectInputRef = useRef<HTMLButtonElement>(null);

  const { isFocusVisible } = useFocusVisible({});

  const { hoverProps, isHovered } = useHover({ isDisabled: coreProps.isDisabled });

  const { focusWithinProps } = useFocusWithin({
    isDisabled: coreProps.isDisabled,
    onFocusWithinChange: (isFocusWithin) => {
      setFocusWithin(isFocusWithin);
      if (!isFocusWithin && isEditing) {
        if (exitHandledRef.current) {
          exitHandledRef.current = false;
          setIsEditing(false);
          return;
        }
        if (isSelectOpenRef.current) return;
        setIsEditing(false);
        ref.current?.closest('form')?.requestSubmit();
      }
    },
  });

  // Detect Escape in capture phase before RAC closes the dropdown
  useEffect(() => {
    if (!isSelectOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        escapeCloseRef.current = true;
      }
    };
    document.addEventListener('keydown', handleKeyDown, true);
    return () => document.removeEventListener('keydown', handleKeyDown, true);
  }, [isSelectOpen]);

  return (
    <View
      ref={ref}
      onFocus={focusWithinProps.onFocus}
      onBlur={focusWithinProps.onBlur}
      style={[styles.base, style]}>
      <SelectCore
        isOpen={isSelectOpen}
        data-hovered={isHovered || undefined}
        data-editing={isEditing || undefined}
        onPointerEnter={(e) => {
          hoverProps.onPointerEnter?.(e);
          onPointerEnter?.(e);
        }}
        onPointerLeave={(e) => {
          hoverProps.onPointerLeave?.(e);
          onPointerLeave?.(e);
        }}
        onOpenChange={(isOpen) => {
          if (isOpen) return; // Only allow opening via action button
          setIsSelectOpen(false);
          isSelectOpenRef.current = false;
          if (isEditing) {
            exitHandledRef.current = true;
            setIsEditing(false);
            if (escapeCloseRef.current) {
              escapeCloseRef.current = false;
              ref.current?.closest('form')?.reset();
            } else {
              ref.current?.closest('form')?.requestSubmit();
            }
          }
          onOpenChange?.(isOpen);
        }}
        {...stylex.props(
          selectFieldStyles.base,
          variant && selectFieldStyles[variant],
          isEditing && selectFieldStyles.editing,
          isEditing && coreProps.isInvalid && selectFieldStyles.invalid)}
        {...coreProps}>
        {(renderProps) => (
          <Provider values={[
            [LabelContext, { color: isHovered || isEditing || (isFocusWithin && isFocusVisible) ? 'primary' : 'tertiary', size: 14, style: labelStyles.base }],
            [SelectInputStyleContext, {
              ref: selectInputRef,
              size: 32,
              style: [selectInputStyles.base, isEditable && !isEditing && selectInputStyles.padded, hasPreview && !isEditing && selectInputStyles.hidden],
              // @ts-expect-error-next-line
              excludeFromTabOrder: hasPreview && !isEditing,
              children: (renderProps: { defaultChildren: React.ReactNode }) => renderProps.defaultChildren,
            }],
            [HintContext, { style: [hintStyles.base, !isEditing && coreProps.isInvalid && hintStyles.hidden] }],
            [TooltipContext, {
              isOpen: (isHovered || (isFocusWithin && isFocusVisible)) && !isEditing,
              triggerRef: selectInputRef as React.RefObject<Element>,
            }],
            [TooltipTriggerStateContext, {
              isOpen: (isHovered || (isFocusWithin && isFocusVisible)) && !isEditing,
              open() { /* controlled by isHovered */ },
              close() { /* controlled by isHovered */ },
            }],
            [PopoverContext, {
              placement: "bottom right",
              offset: 5
            }],
            [ListBoxContext, { style: listBoxStyles.base }],
            [IconButtonContext, {
              slots: {
                [DEFAULT_SLOT]: {},
                [INLINE_TABLE_SELECT_SLOTS.ACTION_BUTTON]: {
                  ref: actionButtonRef,
                  variant: 'ghostLowContrast',
                  size: 32,
                  "aria-label": "Edit",
                  style: iconButtonStyles.actionButton,
                  children: <MoreVerticalStrokeIcon />,
                  onPress: () => {
                    if (isSelectOpen) {
                      setIsSelectOpen(false);
                      isSelectOpenRef.current = false;
                      exitHandledRef.current = true;
                      setIsEditing(false);
                      ref.current?.closest('form')?.requestSubmit();
                    } else {
                      exitHandledRef.current = false;
                      setIsEditing(true);
                      setIsSelectOpen(true);
                      isSelectOpenRef.current = true;
                    }
                  },
                }
              }
            }],
            [SkeletonContext, { style: skeletonStyles.base }],
            [ViewContext, {
              slots: {
                [DEFAULT_SLOT]: {},
                [INLINE_TABLE_SELECT_SLOTS.PREVIEW]: {
                  ref: previewRef,
                  "aria-hidden": true,
                  style: [previewStyles.base, isEditable && previewStyles.padded, isEditing && previewStyles.hidden],
                }
              }
            }],
          ]}>
            <Provider values={[
              [TextContext, {
                slot: null!,
                size: 14,
              }],
              [IconContext, {
                slots: {
                  [DEFAULT_SLOT]: {},
                  [INLINE_TABLE_SELECT_SLOTS.INDICATOR_ICON]: {
                    "aria-hidden": true,
                    size: 16,
                    style: indicatorStyles.base,
                    ...(variant && variant !== 'primary' && { color: VARIANT_INDICATOR_COLOR[variant] }),
                    ...(variant && variant !== 'primary' && { children: VARIANT_INDICATOR_ICON[variant] }),
                  }
                }
              }],
            ]}>
              {typeof children === 'function' ? children?.(renderProps) : children}
            </Provider>
          </Provider>
        )}
      </SelectCore>
    </View>
  );
});

const styles = stylex.create({
  base: {
    borderRadius: 6,
    gridColumn: '1 / -1',
    alignItems: 'center',
    display: 'grid',
    gridTemplateColumns: 'subgrid',
  },
});

const selectFieldStyles = stylex.create({
  base: {
    borderRadius: 4,
    gridColumn: '1 / -1',
    alignItems: 'flex-start',
    display: 'grid',
    gridTemplateColumns: 'subgrid',
    position: 'relative',
    minHeight: 36,
  },
  primary: {},
  warning: {
    backgroundColor: $.surfaceAttentiveSilent,
  },
  success: {
    backgroundColor: $.surfacePositiveSilent,
  },
  error: {
    backgroundColor: $.surfaceCriticalSilent,
  },
  editing: {
    backgroundColor: $.surfaceHighlightSilent,
  },
  invalid: {
    backgroundColor: $.surfaceCriticalSilent,
  }
});

const labelStyles = stylex.create({
  base: {
    paddingInline: 8,
    alignItems: 'flex-start',
    transitionDuration: '0.2s',
    transitionProperty: 'color',
    transitionTimingFunction: 'easy-in-out',
    height: 36,
    paddingTop: 10,
  }
});

const selectInputStyles = stylex.create({
  base: {
    borderStyle: 'none',
    backgroundColor: 'transparent',
    cursor: null,
    height: 36,
    minWidth: 0,
    paddingBottom: 6,
    paddingLeft: 8,
    paddingRight: 8,
    paddingTop: 6,
  },
  padded: {
    paddingRight: 40,
  },
  hidden: {
    opacity: 0,
    pointerEvents: 'none',
    position: 'absolute',
  },
});

const listBoxStyles = stylex.create({
  base: {
    padding: 4,
    borderRadius: 10,
    backgroundColor: $.surfaceBGPrimary,
    boxShadow: $.shadowCenter070Loud,
  }
});

const previewStyles = stylex.create({
  base: {
    gap: 8,
    overflow: 'hidden',
    alignItems: 'center',
    display: 'flex',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    minHeight: 36,
    minWidth: 0,
    paddingBottom: 6,
    paddingLeft: 8,
    paddingRight: 8,
    paddingTop: 6,
  },
  padded: {
    paddingRight: 40,
  },
  hidden: {
    display: 'none',
  },
});

const iconButtonStyles = stylex.create({
  actionButton: {
    position: 'absolute',
    right: 2,
    top: 2,
  },
});

const hintStyles = stylex.create({
  base: {
    gridColumn: '2',
    paddingBottom: 4,
    paddingLeft: 8,
    paddingRight: 8,
  },
  hidden: {
    display: 'none',
  }
});

const skeletonStyles = stylex.create({
  base: {
    marginInline: 8,
    width: 'auto',
  },
});

const indicatorStyles = stylex.create({
  base: {
    marginLeft: 'auto',
    marginRight: 8,
  },
});

const VARIANT_INDICATOR_ICON = {
  warning: <EditStrokeIcon />,
  success: <CheckmarkStrokeIcon />,
  error: <CloseStrokeIcon />,
};

const VARIANT_INDICATOR_COLOR = {
  warning: 'attentive',
  success: 'positive',
  error: 'destructive',
} as const;

const INLINE_TABLE_SELECT_SLOTS = {
  PREVIEW: 'preview',
  INDICATOR_ICON: 'indicator',
  ACTION_BUTTON: 'action-button',
};

export { INLINE_TABLE_SELECT_SLOTS, InlineTableSelect, InlineTableSelectContext, type InlineTableSelectProps };
